import { Unstable_Grid2 as Grid2, MenuItem, Stack, Typography, Chip, Box } from "@mui/material";
import React from "react";
// import { useTranslation } from "react-i18next";
import { IoSearch, IoClose } from "react-icons/io5";
import { useTheme } from "@emotion/react";
import { motion, AnimatePresence } from "framer-motion";
import AutobiaInput from "./AutobiaInput";
import AutobiaSelect from "./AutobiaSelect";

const AutobiaFilterSection = ({ 
  filters = {},
  onFilterChange = () => {},
  filterConfig = [],
  showActiveFilters = true,
  variant = "default", // "default", "compact", "minimal"
  spacing = "0.5rem",
  backgroundColor = "background.paper",
  elevation = 4
}) => {
  //SECTION - general
  const t = (key) => key; // Fallback translation
  const i18n = { language: "en" }; // Fallback i18n
  const theme = useTheme();

  //SECTION - functions
  const handleFilterChange = (key, value) => {
    onFilterChange({
      ...filters,
      [key]: value,
      ...(key !== "page" && { page: 1 }) // Reset page when filter changes
    });
  };

  const clearFilter = (key, defaultValue = "") => {
    handleFilterChange(key, defaultValue);
  };

  const getActiveFilters = () => {
    return filterConfig.filter(config => {
      const value = filters[config.key];
      const isActive = !["", 0, null, "empty", undefined].includes(value) &&
        !(Array.isArray(value) && value.length === 0);
      return isActive;
    });
  };

  const getFilterDisplayValue = (config, value) => {
    if (config.type === "select" && config.options) {
      const option = config.options.find(opt => opt.value === value);
      return option ? option[`name_${i18n.language}`] || option.label : value;
    }
    if (config.type === "autocomplete" && Array.isArray(value)) {
      return value.map(item => item.name || item.label || item).join(", ");
    }
    return value?.toString() || "";
  };

  const renderFilterInput = (config) => {
    const { key, type, title, placeholder, icon, options = [], ...props } = config;
    const value = filters[key] || (type === "autocomplete" ? [] : "");

    switch (type) {
      case "select":
        return (
          <AutobiaSelect
            key={key}
            title={title}
            value={value}
            onChange={(e) => handleFilterChange(key, e.target.value)}
            {...props}
          >
            <MenuItem value="">
              <Typography sx={{ fontStyle: "italic", color: "text.secondary" }}>
                {placeholder || t ? t("placeholder.selectOption") : "Select option"}
              </Typography>
            </MenuItem>
            {options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option[`name_${i18n.language}`] || option.label}
              </MenuItem>
            ))}
          </AutobiaSelect>
        );

      case "autocomplete":
        // For autocomplete, you'd need to implement AutobiaAutocomplete component
        return (
          <Box key={key}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {title}
            </Typography>
            {/* Placeholder for autocomplete - implement when needed */}
            <Typography variant="body2" sx={{ fontStyle: "italic" }}>
              Autocomplete component needed
            </Typography>
          </Box>
        );

      default: // text input
        return (
          <AutobiaInput
            key={key}
            title={title}
            icon={icon || <IoSearch />}
            placeholder={placeholder}
            value={value}
            onChange={(e) => handleFilterChange(key, e.target.value)}
            {...props}
          />
        );
    }
  };

  const getGridSize = () => {
    switch (variant) {
      case "compact":
        return { xs: 12, sm: 6, md: 3 };
      case "minimal":
        return { xs: 12, sm: 4, md: 2.4 };
      default:
        return { xs: 12, md: 4 };
    }
  };

  return (
    <Grid2
      sx={{
        backgroundColor,
        borderRadius: "15px",
        padding: variant === "minimal" ? "1rem" : "2rem",
        boxShadow: theme.shadows[elevation],
        width: "100%",
      }}
      direction={"row"}
      spacing={spacing}
      container
    >
      {/* Filter Inputs */}
      {filterConfig.map((config) => (
        <Grid2 key={config.key} size={getGridSize()}>
          {renderFilterInput(config)}
        </Grid2>
      ))}

      {/* Active Filters Display */}
      {showActiveFilters && (
        <Grid2 container item size={12} spacing={"0.5rem"} sx={{ mt: 1 }}>
          <AnimatePresence mode="popLayout">
            {getActiveFilters().map((config) => (
              <motion.div
                key={config.key}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <Chip
                  label={
                    <Stack direction="row" alignItems="center" gap="0.25rem">
                      <Typography variant="body2" fontWeight={600}>
                        {t ? t(config.labelKey || config.title) : config.title}:
                      </Typography>
                      <Typography variant="body2">
                        {getFilterDisplayValue(config, filters[config.key])}
                      </Typography>
                    </Stack>
                  }
                  onDelete={() => clearFilter(config.key, config.defaultValue)}
                  deleteIcon={<IoClose />}
                  variant="outlined"
                  color="secondary"
                  sx={{
                    backgroundColor: theme.palette.secondary?.light || "#f0f0f0",
                    borderColor: theme.palette.secondary?.main || "#1976d2",
                    "& .MuiChip-deleteIcon": {
                      color: theme.palette.secondary?.main || "#1976d2",
                      "&:hover": {
                        color: theme.palette.secondary?.dark || "#1565c0",
                      },
                    },
                  }}
                  component={motion.div}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                />
              </motion.div>
            ))}
          </AnimatePresence>
        </Grid2>
      )}
    </Grid2>
  );
};

export default AutobiaFilterSection;